import { useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { Center, useTheme } from '@chakra-ui/react'
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Text,
  Heading,
  Flex,
  Image,
  Icon,
  ModalFooter,
  DataTable,
  Progress,
  Stack
} from '@homebotapp/hb-react-component-catalog'
import { BUY_DOWN_MAPPINGS, BUY_DOWN_TYPE } from './BuyDownType'
import { BuyDownLearnMoreModal } from './BuyDownLearnMoreModal'
import { calculateMonthlyPayment } from '../../Utilities/EstimatedMonthlyPaymentUtilities'
import { formatCurrencyWithOpts } from '../../../../../../helpers/formatCurrencyWithOpts'
import { BuyDownOpenMessage } from './BuyDownOpenMessage'
import { useCreateClientEvent } from '../../../../../../hooks'
import { ClientEventName, ClientEventSource } from '../../../../../../api/gqlaxy/gql/generated/graphql'

const MSG = defineMessages({
  buyDownDetails: {
    id: 'BuyDownDetailsModal.buyDownDetails',
    defaultMessage: 'See buydown details'
  },
  buyDownDetailsWithType: {
    id: 'BuyDownDetailsModal.buyDownDetailsWithType',
    defaultMessage: '{buyDownType} buydown details'
  },
  close: {
    id: 'BuyDownDetailsModal.close',
    defaultMessage: 'Close'
  },
  haveAQuestion: {
    id: 'BuyDownDetailsModal.haveAQuestion',
    defaultMessage: 'Have a question?'
  },
  youCouldSave: {
    id: 'BuyDownDetailsModal.youCouldSave',
    defaultMessage: 'You could save'
  },
  yearWithNumber: {
    id: 'BuyDownDetailsModal.yearWithNumber',
    defaultMessage: 'Year {year}'
  },
  year: {
    id: 'BuyDownDetailsModal.year',
    defaultMessage: 'Year'
  },
  years: {
    id: 'BuyDownDetailsModal.years',
    defaultMessage: 'Years {year}'
  },
  rate: {
    id: 'BuyDownDetailsModal.rate',
    defaultMessage: 'Rate'
  },
  payment: {
    id: 'BuyDownDetailsModal.payment',
    defaultMessage: 'Payment'
  },
  youSave: {
    id: 'BuyDownDetailsModal.youSave',
    defaultMessage: 'You Save'
  },
  fullRateMonthlyPayment: {
    id: 'BuyDownDetailsModal.fullRateMonthlyPayment',
    defaultMessage: 'Full rate monthly payment of {fullRateMonthlyPayment}'
  },
  yourMonthlyPayment: {
    id: 'BuyDownDetailsModal.yourMonthlyPayment',
    defaultMessage: 'Your monthly payment'
  },
  subsidizedPortion: {
    id: 'BuyDownDetailsModal.subsidizedPortion',
    defaultMessage: 'Subsidized portion'
  },
  totalAmountPaidUpFront: {
    id: 'BuyDownDetailsModal.totalAmountPaidUpFront',
    defaultMessage: 'This is the total amount paid up front to subsidize your monthly payments.'
  },
  monthlyPaymentBreakdown: {
    id: 'BuyDownDetailsModal.monthlyPaymentBreakdown',
    defaultMessage: 'Monthly payment breakdown'
  }
})

export interface BuyDownDetailsModalProps {
  buyDownType: BUY_DOWN_TYPE
  listingId?: string
  loanAmount: number
  loanTerm: number
  rate: number
}

export const BuyDownDetailsModal = ({
  buyDownType,
  listingId,
  loanAmount,
  loanTerm,
  rate
}: BuyDownDetailsModalProps) => {
  const intl = useIntl()
  const [showModal, setShowModal] = useState(false)

  /**
   * Data to calculate for buydown details
   * 1. Year one monthly payment
   * 2. X years of monthly payment
   * 3. Non subsidized monthly payment
   * 4. Total savings
   */
  const fullRateMonthlyPayment = calculateMonthlyPayment(loanAmount, rate, loanTerm)
  const monthlyPaymentArray: number[] = []
  let totalSavings = 0

  const data: string[][] = []
  BUY_DOWN_MAPPINGS[buyDownType].forEach(({ year, rateDrop }) => {
    const buyDownMonthlyPayment = calculateMonthlyPayment(loanAmount, rate - rateDrop, loanTerm)
    const monthlySavings = fullRateMonthlyPayment - buyDownMonthlyPayment

    // Only add to total savings if there are actual savings (rateDrop > 0)
    if (rateDrop > 0) {
      totalSavings += monthlySavings * 12
    }

    data.push([
      year.toString(),
      (rate - rateDrop).toFixed(1) + '%',
      formatCurrencyWithOpts(buyDownMonthlyPayment, { minorDigits: true }),
      formatCurrencyWithOpts(monthlySavings, { minorDigits: true })
    ])
    monthlyPaymentArray.push(buyDownMonthlyPayment)
  })

  const { mutateAsync: createClientEvent } = useCreateClientEvent()

  return (
    <>
      <Button
        onClick={() => {
          setShowModal(true)
          createClientEvent({
            clientEventInput: {
              eventName: ClientEventName.OpenedBuyDownDetails,
              eventSource: listingId ? ClientEventSource.ListingDetailed : ClientEventSource.ListingsSearch,
              ...(listingId && { listingId })
            }
          })
        }}
        w='100%'
        mt={2}
        tracking={{
          guid: '11utLU4M8cyfK7NproxQjt',
          ui_context: 'Buy Down Details Modal',
          descriptor: 'Opened Buy Down Details Modal'
        }}
      >
        {intl.formatMessage(MSG.buyDownDetails)}
        <Icon name='circle-arrow-right-bts' ml={2} />
      </Button>

      <Modal isOpen={showModal} size={'full'} onClose={() => setShowModal(false)}>
        <ModalOverlay />
        <ModalContent overflow='hidden' borderRadius={16} marginInline={[0, 5, '20%']} marginTop={[0, 6]} mb={[0, 8]}>
          <ModalHeader>
            <Heading as={'h3'} size={'lg'} textAlign={'center'} mb={2}>
              {intl.formatMessage(MSG.buyDownDetailsWithType, { buyDownType: buyDownType })}
            </Heading>
            <Center>
              <BuyDownLearnMoreModal detailPage={true} listingId={listingId} />
            </Center>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box maxW={'30rem'} mx='auto'>
              {/* Add your buy down details content here */}
              <Heading as={'h3'} size={'sm'} textAlign={'center'} variant={'eyebrow'} mb={1}>
                {intl.formatMessage(MSG.youCouldSave)}
              </Heading>
              <Text color='brand.600' fontWeight='bold' size='xl' textAlign={'center'} mb={2}>
                {formatCurrencyWithOpts(totalSavings, {
                  minorDigits: false
                })}
              </Text>
              <Text color='neutral.700' textAlign={'center'} size={'xs'} mt={0} maxW={64} mx={'auto'} mb={8}>
                {intl.formatMessage(MSG.totalAmountPaidUpFront)}
              </Text>
              {/* Map through monthlyPaymentArray, show bar of payment out of fullRateMonthly */}
              <Stack gap={2} mt={12}>
                {monthlyPaymentArray.map((payment, index) => {
                  const isLast = index === monthlyPaymentArray.length - 1
                  const yearDisplay = isLast ? `${index + 1}+` : index + 1
                  return (
                    <Flex key={index} flexDir={'row'} alignItems={'center'} gap={2} w='100%'>
                      <Box flex='0 1 auto' minW={16} alignItems={'center'}>
                        <Text mb={0} color={'neutral.900'} fontWeight={'semibold'} size={'sm'}>
                          {intl.formatMessage(isLast ? MSG.years : MSG.yearWithNumber, { year: yearDisplay })}
                        </Text>
                      </Box>
                      <Box alignItems={'center'} flex='1'>
                        {isLast ? (
                          <Progress
                            value={100}
                            colorScheme='success.700'
                            aria-label={intl.formatMessage(MSG.fullRateMonthlyPayment, { fullRateMonthlyPayment })}
                            height={4}
                          />
                        ) : (
                          <Progress
                            values={{
                              'success.700': payment,
                              'neutral.600': fullRateMonthlyPayment - payment
                            }}
                            height={4}
                            min={0}
                            max={fullRateMonthlyPayment}
                            variant='multiSegment'
                            aria-label={`${payment} out of ${fullRateMonthlyPayment}`}
                          />
                        )}
                      </Box>
                    </Flex>
                  )
                })}
                <Flex gap={4} mt={4} mb={2}>
                  <Flex alignItems={'center'} gap={2} flex='1'>
                    <Box w={3} h={3} borderRadius={'full'} bg={'success.700'} />
                    <Text mb={0} fontWeight={'semibold'} color={'neutral.900'} size={'sm'}>
                      {intl.formatMessage(MSG.yourMonthlyPayment)}
                    </Text>
                  </Flex>
                  <Flex alignItems={'center'} gap={2} flex='1'>
                    <Box w={3} h={3} borderRadius={'full'} bg={'neutral.600'} />
                    <Text mb={0} fontWeight={'semibold'} color={'neutral.900'} size={'sm'}>
                      {intl.formatMessage(MSG.subsidizedPortion)}
                    </Text>
                  </Flex>
                </Flex>
              </Stack>

              <Box mt={12}>
                <Heading as={'h3'} size={'sm'} textAlign={'center'} mb={1} fontWeight={'bold'}>
                  {intl.formatMessage(MSG.monthlyPaymentBreakdown)}
                </Heading>
                <DataTable
                  variant='simple'
                  data={data}
                  headers={[
                    intl.formatMessage(MSG.year),
                    intl.formatMessage(MSG.rate),
                    intl.formatMessage(MSG.payment),
                    intl.formatMessage(MSG.youSave)
                  ]}
                />
              </Box>
            </Box>
          </ModalBody>
          <ModalFooter justifyContent={'flex-start'}>
            <Box width={'100%'} maxW={'30rem'} mx='auto'>
              <BuyDownOpenMessage listingId={listingId} />
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
